/**
 * Risk Level & Status Styling Utilities
 *
 * Centralized styling for risk levels (high, medium, low) and statuses.
 * Uses design tokens from tailwind.config.js.
 *
 * @example
 * import { getRiskStyles, getStatusColor } from '../components/ui';
 *
 * const styles = getRiskStyles('high');
 * // Returns: { text, bg, bgLight, border, ring, dot }
 *
 * const dotColor = getStatusColor('active');
 * // Returns: 'bg-risk-low-600'
 */

/**
 * Get all risk-related styles for a given level
 * @param {string} risk - Risk level: 'high', 'medium', 'moderate', 'low'
 * @returns {object} Object with text, bg, bgLight, border, ring, dot classes
 */
export function getRiskStyles(risk) {
  const level = risk?.toLowerCase()?.trim();

  const styles = {
    high: {
      text: 'text-risk-high-600',
      textDark: 'text-risk-high-700',
      bg: 'bg-risk-high-600',
      bgLight: 'bg-risk-high-50',
      bgMedium: 'bg-risk-high-100',
      border: 'border-risk-high-200',
      ring: 'ring-risk-high-200',
      dot: 'bg-risk-high-600',
    },
    medium: {
      text: 'text-risk-medium-500',
      textDark: 'text-risk-medium-600',
      bg: 'bg-risk-medium-500',
      bgLight: 'bg-risk-medium-50',
      bgMedium: 'bg-risk-medium-100',
      border: 'border-risk-medium-200',
      ring: 'ring-risk-medium-200',
      dot: 'bg-risk-medium-500',
    },
    moderate: {
      text: 'text-risk-medium-500',
      textDark: 'text-risk-medium-600',
      bg: 'bg-risk-medium-500',
      bgLight: 'bg-risk-medium-50',
      bgMedium: 'bg-risk-medium-100',
      border: 'border-risk-medium-200',
      ring: 'ring-risk-medium-200',
      dot: 'bg-risk-medium-500',
    },
    low: {
      text: 'text-risk-low-600',
      textDark: 'text-risk-low-700',
      bg: 'bg-risk-low-600',
      bgLight: 'bg-risk-low-50',
      bgMedium: 'bg-risk-low-100',
      border: 'border-risk-low-200',
      ring: 'ring-risk-low-200',
      dot: 'bg-risk-low-600',
    },
  };

  // Default/neutral styles
  const defaultStyles = {
    text: 'text-neutral-600',
    textDark: 'text-neutral-700',
    bg: 'bg-neutral-400',
    bgLight: 'bg-neutral-50',
    bgMedium: 'bg-neutral-100',
    border: 'border-neutral-200',
    ring: 'ring-neutral-200',
    dot: 'bg-neutral-400',
  };

  return styles[level] || defaultStyles;
}

/**
 * Individual getter functions for backward compatibility
 * These can be used if you only need one style property
 */
export const getRiskTextColor = (risk) => getRiskStyles(risk).text;
export const getRiskBgColor = (risk) => getRiskStyles(risk).bgMedium;
export const getRiskBgLightColor = (risk) => getRiskStyles(risk).bgLight;
export const getRiskBorderColor = (risk) => getRiskStyles(risk).border;
export const getRiskRingColor = (risk) => getRiskStyles(risk).ring;

/**
 * Get status dot color for driver/order statuses
 * @param {string} status - Status: 'active', 'pending', 'review', 'created', 'released'
 * @returns {string} Tailwind background color class
 */
export function getStatusColor(status) {
  const statusColors = {
    active: 'bg-risk-low-600',      // Green
    released: 'bg-risk-low-600',    // Green
    pending: 'bg-risk-medium-500',  // Orange/Yellow
    created: 'bg-risk-medium-500',  // Orange/Yellow
    review: 'bg-risk-high-600',     // Red
  };

  return statusColors[status?.toLowerCase()] || 'bg-neutral-300';
}
